// src/pages/NotesPage/NotesPage.jsx
import React, { useEffect, useState } from "react";
import { Form, Button, Row, Col, Badge, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  listNotes,
  addPetNote,
  deletePetNote,
  formatNoteTimestamp,
} from "../../lib/petNotes";
import { Storage } from "aws-amplify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import "./NotesPage.css";
import PetPageHeader from "../../components/PetPageHeader";
import FloatingPawButton from "../../components/FloatingPawButton";

export default function NotesPage() {
  const navigate = useNavigate();
  const [tagId, setTagId] = useState("");
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [content, setContent] = useState("");
  const [category, setCategory] = useState("Daily");
  const [mood, setMood] = useState("Normal");
  const [photoFile, setPhotoFile] = useState(null);

  const [deletingSk, setDeletingSk] = useState(null);

  // Load tag from localStorage
  useEffect(() => {
    const storedTag = (localStorage.getItem("lpp.tagId") || "").toUpperCase();
    if (!storedTag) {
      navigate("/profile");
      return;
    }
    setTagId(storedTag);
  }, [navigate]);

  // Load notes when tag is ready
  useEffect(() => {
    if (!tagId) return;

    const loadNotes = async () => {
      try {
        setLoading(true);
        const items = await listNotes(tagId);

        const withPhotos = await Promise.all(
          (items || []).map(async (n) => {
            if (!n.photo) return n;
            try {
              const url = await Storage.get(n.photo);
              return { ...n, photoUrl: url };
            } catch {
              return n;
            }
          })
        );

        setNotes(withPhotos);
      } catch (err) {
        console.error("Failed to load notes:", err);
      } finally {
        setLoading(false);
      }
    };

    loadNotes();
  }, [tagId]);

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;

    try {
      setSaving(true);
      const newNote = await addPetNote({
        tagId,
        content: content.trim(),
        category,
        mood,
        photoFile,
      });

      let enriched = newNote;
      if (newNote.photo) {
        try {
          const url = await Storage.get(newNote.photo);
          enriched = { ...newNote, photoUrl: url };
        } catch (err) {
          console.error("Failed to resolve photo URL:", err);
        }
      }

      setNotes((prev) => [enriched, ...prev]);
      setContent("");
      setMood("Normal");
      setCategory("Daily");
      setPhotoFile(null);
    } catch (err) {
      console.error("Failed to add note:", err);
      alert("Could not save that note. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteNote = async (note) => {
    if (!note.sk) return;

    const confirmed = window.confirm(
      "Delete this note from your pet's journal? This cannot be undone."
    );
    if (!confirmed) return;

    try {
      setDeletingSk(note.sk);
      await deletePetNote(tagId, note.sk);
      setNotes((prev) => prev.filter((n) => n.sk !== note.sk));
    } catch (err) {
      console.error("Failed to delete note:", err);
      alert("Could not delete that note. Please try again.");
    } finally {
      setDeletingSk(null);
    }
  };

  return (
    <div className="notes-wrapper">
      <div className="notes-shell">
        <PetPageHeader
          baseTitle="Notes"
          subtitle="Keep quick notes about meds, behavior, and vet conversations."
        />

        <main className="notes-body">
          {/* Add Note Form */}
          <section className="notes-form-section">
            <Form onSubmit={handleAddNote}>
              <Form.Group className="mb-3">
                <Form.Label className="notes-label">Note</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="e.g., Bentley ate well today and had a long walk."
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  className="notes-textarea"
                />
              </Form.Group>

              <Row className="mb-3 g-3">
                <Col xs={6} md={4}>
                  <Form.Group>
                    <Form.Label className="notes-label">Category</Form.Label>
                    <Form.Select
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      className="notes-select"
                    >
                      <option>Daily</option>
                      <option>Behavior</option>
                      <option>Feeding</option>
                      <option>Medications</option>
                      <option>Grooming</option>
                      <option>Other</option>
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col xs={6} md={4}>
                  <Form.Group>
                    <Form.Label className="notes-label">Mood</Form.Label>
                    <Form.Select
                      value={mood}
                      onChange={(e) => setMood(e.target.value)}
                      className="notes-select"
                    >
                      <option>Happy</option>
                      <option>Normal</option>
                      <option>Concerned</option>
                      <option>Sick</option>
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="notes-label">Photo (optional)</Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={(e) =>
                        setPhotoFile(e.target.files ? e.target.files[0] : null)
                      }
                      className="notes-file-input"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <div className="d-flex justify-content-end">
                <Button
                  type="submit"
                  disabled={saving || !content.trim()}
                  className="lpp-pill-btn"
                >
                  {saving ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Saving…
                    </>
                  ) : (
                    "Add Note"
                  )}
                </Button>
              </div>
            </Form>
          </section>

          <hr className="notes-divider" />

          {/* Timeline */}
          <section>
            <h5 className="notes-section-title mb-3">Timeline</h5>

            {loading ? (
              <div className="d-flex align-items-center">
                <Spinner animation="border" size="sm" className="me-2" />
                <span className="text-muted small">Loading notes…</span>
              </div>
            ) : notes.length === 0 ? (
              <p className="text-muted small mb-0">
                No notes yet. Start by adding how your pet is doing today.
              </p>
            ) : (
              <div className="notes-timeline">
                {notes.map((note, idx) => {
                  const when = note.timestamp || (note.sk && note.sk.replace("NOTE#", ""));
                  const displayTime = formatNoteTimestamp(when);

                  return (
                    <div
                      key={note.sk}
                      className={`notes-timeline-item ${
                        idx % 2 === 1 ? "notes-row-alt" : ""
                      }`}
                    >
                      <div className="notes-timeline-dot" />

                      <div className="notes-timeline-content">
                        <div className="d-flex align-items-center justify-content-between mb-1 flex-wrap gap-2">
                          <div className="d-flex align-items-center flex-wrap gap-1">
                            <span className="small text-muted me-2">
                              {displayTime}
                            </span>

                            {note.category && (
                              <Badge bg="dark" pill className="me-1">
                                {note.category}
                              </Badge>
                            )}

                            {note.mood && (
                              <Badge
                                bg={
                                  note.mood === "Happy"
                                    ? "success"
                                    : note.mood === "Concerned" || note.mood === "Sick"
                                    ? "danger"
                                    : "warning"
                                }
                                pill
                              >
                                {note.mood}
                              </Badge>
                            )}
                          </div>

                          {/* ✅ Icon delete button */}
                          <button
                            type="button"
                            className="notes-icon-delete"
                            onClick={() => handleDeleteNote(note)}
                            disabled={deletingSk === note.sk}
                            title="Delete note"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>

                        <div className="mb-1 notes-content-text">{note.content}</div>

                        {note.photoUrl && (
                          <div className="notes-photo-wrapper mt-1">
                            <img
                              src={note.photoUrl}
                              alt="Pet note"
                              className="notes-photo"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </main>

        <div>
          <FloatingPawButton isAuthenticated={true} hasActiveSub={true} />
        </div>
      </div>
    </div>
  );
}
